import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { supportsImageFormat } from '@fias/canvas-kit';
import type { CanvasNativeExportFormat, ExportFormat, ExportSettings } from '../types';
import { slugifySliceName, type SliceExportFormat } from '../lib/slice-export';
import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';
import {
  EXPORT_FORMAT_OPTIONS,
  isClipboardUnsupportedFormat,
  isLosslessRasterFormat,
  isSlicesIncompatibleFormat,
  isStructuredFormat,
  isWiredFormat,
} from '../lib/export-formats';
import {
  BUILT_IN_EXPORT_PRESETS,
  CUSTOM_PRESET,
  type BuiltInPresetId,
  findBuiltInPreset,
  matchPresetByDimensions,
} from '../lib/export-presets';
import {
  listUserPresets,
  saveUserPreset,
  deleteUserPreset,
  USER_EXPORT_PRESET_NAME_MAX,
  type UserExportPreset,
} from '../lib/user-export-presets';

/** Whether the browser supports writing image blobs to the OS
 *  clipboard. PNG is the only format with broad support; the dialog
 *  surfaces the option only when this is true. */
const SUPPORTS_CLIPBOARD_WRITE =
  typeof navigator !== 'undefined' &&
  typeof navigator.clipboard?.write === 'function' &&
  typeof ClipboardItem !== 'undefined';

/** Whether the browser supports the Web Share API with files. Used on
 *  mobile (coarse-pointer) to surface "Share" instead of "Clipboard"
 *  — same destination button, more useful affordance on touch devices. */
const SUPPORTS_FILE_SHARE =
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  typeof navigator.canShare === 'function';

const IS_COARSE_POINTER =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/** Whether the host browser can encode AVIF via the canvas API.
 *  Chrome/Edge stable, Firefox 113+, Safari 16.4+ (probe semantics —
 *  SSR guards, 1×1 canvas, PNG-fallback prefix check — live in
 *  canvas-kit's `supportsImageFormat`).
 *
 *  Evaluated once at module load (matches the other SUPPORTS_*
 *  probes above; canvas-kit deliberately does not cache). The
 *  dropdown disables the AVIF option dynamically when this is false,
 *  so the user can see the option but can't select something the
 *  browser can't deliver. */
const SUPPORTS_AVIF = supportsImageFormat('image/avif');

/** True when the third destination button should be shown at all. */
export const SUPPORTS_CLIPBOARD_OR_SHARE = SUPPORTS_CLIPBOARD_WRITE || SUPPORTS_FILE_SHARE;

export type ExportDestination = 'local' | 'fias' | 'clipboard';

/** Scale radio options. Custom unlocks the W/H inputs; the preset
 *  scales lock dimensions to canvas × multiplier. */
type ScaleMode = '1x' | '2x' | '3x' | 'custom';
const SCALE_MULTIPLIERS: Record<Exclude<ScaleMode, 'custom'>, number> = {
  '1x': 1,
  '2x': 2,
  '3x': 3,
};

/** Region options surfaced in the dropdown. All five values are
 *  now wired; the per-mode "currently exportable?" gate is computed
 *  at render time from the dialog's props (selection presence,
 *  active layer, any-layer-visible, slice count) and applied to the
 *  `<option disabled>` attribute below. We keep `disabled: false`
 *  in this constant so the type guard `isWiredRegion` accepts every
 *  value — runtime gating handles the rest. */
type RegionMode = 'whole' | 'selection' | 'active-layer' | 'visible' | 'slices';
/** Single-image sub-region modes (everything except `whole` and
 *  `slices`). Routes through the shared `onExportRegion` callback —
 *  the format dispatch + destination handling is identical for all
 *  three; only the bounds-resolution logic differs (handled in
 *  `region-export.ts`). */
export type SubRegionMode = 'selection' | 'active-layer' | 'visible';
interface RegionOption {
  value: RegionMode;
  label: string;
  disabled: boolean;
}
const REGION_OPTIONS: readonly RegionOption[] = [
  { value: 'whole', label: 'Whole canvas', disabled: false },
  { value: 'selection', label: 'Selection', disabled: false },
  { value: 'active-layer', label: 'Active layer', disabled: false },
  { value: 'visible', label: 'Visible layers', disabled: false },
  // Tier 2 Group F sub-piece #5 — Slices wired. Disabled-state
  // when no slices defined is enforced in the dropdown's per-
  // option `<option disabled>` below via the `sliceCount` prop;
  // we keep `disabled: false` in this constant so the type guard
  // `isWiredRegion` accepts the value once the dialog flips it on.
  { value: 'slices', label: 'Slices', disabled: false },
];

/** Type guard mirroring `isWiredFormat`'s pattern in `export-formats.ts`.
 *  Narrows a dropdown value to the wired runtime region set. The
 *  dropdown's `disabled` attribute prevents disabled options from being
 *  selected through normal interaction, but a future Region surface
 *  (PR 2b's stash-aware export, or the Slice tool sub-piece) might wire
 *  through a non-disabled region value programmatically — this guard
 *  is the narrow-at-the-boundary safety net so TypeScript knows the
 *  state value is a valid `RegionMode`. */
function isWiredRegion(value: string): value is RegionMode {
  const opt = REGION_OPTIONS.find((o) => o.value === value);
  return !!opt && !opt.disabled;
}

/** Section ids drive accordion state on mobile. Order is also the
 *  render order — keep this list in sync with the JSX below. */
type SectionId = 'format' | 'dimensions' | 'destination' | 'scale' | 'region' | 'preset';

interface ExportDialogProps {
  onClose: () => void;
  /** Raster-only callbacks — PSD takes a separate route via
   *  `onExportPsd` because it can't roundtrip through `canvas.toBlob`
   *  and has no notion of quality. */
  onExportLocal: (
    fileName: string,
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => void | Promise<void>;
  onExportFias: (settings: ExportSettings, fileName: string) => void | Promise<void>;
  /** Copy the export to the OS clipboard (desktop) or trigger the
   *  native share sheet (mobile). Optional — dialog hides the
   *  destination option entirely when not provided. */
  onExportClipboard?: (
    fileName: string,
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => void | Promise<void>;
  /** PSD-specific export. Tier 2 Group F sub-piece #2 PR 2b — routed
   *  through `exportDocumentToPsd` (not `canvas.toBlob`). Receives the
   *  destination so EditorPage can decide download vs Fias-upload vs
   *  refuse-clipboard. Optional — dialog falls back to disabling PSD
   *  in the format dropdown when not provided. */
  onExportPsd?: (fileName: string, destination: ExportDestination) => void | Promise<void>;
  /** SVG-specific export. Tier 2 Group F sub-piece #4 — routed
   *  through `exportDocumentToSvg`. Same shape as `onExportPsd`:
   *  destination plumbed through, dialog disables SVG in dropdown
   *  when prop missing. SVG is local-only in v1 (no SVG-to-Fias
   *  upload, no SVG on OS clipboard). */
  onExportSvg?: (fileName: string, destination: ExportDestination) => void | Promise<void>;
  /** PDF-specific export. Tier 2 Group F sub-piece #6 — routed
   *  through `exportDocumentToPdf`. Single-page PDF sized to the
   *  canvas; embedded as JPEG. Same shape as `onExportPsd` /
   *  `onExportSvg`: destination plumbed through, dialog disables
   *  PDF in dropdown when prop missing. PDF is local-only in v1. */
  onExportPdf?: (fileName: string, destination: ExportDestination) => void | Promise<void>;
  /** Slice-region export. Tier 2 Group F sub-piece #5 (raster) +
   *  sub-piece #6 (PDF) — routed through `exportSlicesAsBlobs` +
   *  `bundleSlicesIntoZip` (local) or a per-slice `processImage`
   *  upload loop (Fias). Receives destination plus the resolved
   *  scale multiplier (1/2/3) and the chosen `SliceExportFormat`
   *  (canvas-native raster | 'pdf'). Optional — when not provided
   *  the Slices option in the Region dropdown stays disabled. */
  onExportSlices?: (options: {
    destination: ExportDestination;
    format: SliceExportFormat;
    quality: number;
    scaleMultiplier: number;
    fileName: string;
  }) => void | Promise<void>;
  /** Number of currently-defined slices. Drives the "Slices (N)"
   *  region label + disabled-state when zero. */
  sliceCount?: number;
  /** Sub-region export — Selection / Active Layer / Visible Layers.
   *  All three are single-image outputs that share the same format
   *  dispatch + destination plumbing; only the bounds-resolution
   *  logic differs (see `lib/region-export.ts`).
   *
   *  The handler receives `fileName` verbatim from the File Name
   *  input — the dialog seeds the input with the region's natural
   *  default (`${defaultFileName}-selection` /
   *  `${defaultFileName}-layer-<slug>` / `${defaultFileName}-visible`)
   *  whenever the user picks a region, then the input is the source
   *  of truth. The user can edit the suggestion (e.g. delete the
   *  `-selection` suffix to save as the bare doc name) and the
   *  handler will respect it verbatim.
   *
   *  Optional — when not provided the Selection / Active Layer /
   *  Visible Layers options in the Region dropdown stay disabled. */
  onExportRegion?: (options: {
    region: SubRegionMode;
    destination: ExportDestination;
    format: SliceExportFormat;
    quality: number;
    scaleMultiplier: number;
    fileName: string;
  }) => void | Promise<void>;
  /** Selection-region gate. True when the canvas has a finalized
   *  marquee selection with a non-empty bbox. When false the
   *  Selection option in the Region dropdown stays disabled. */
  hasMarqueeSelection?: boolean;
  /** Active-Layer-region gate. True when an active layer exists AND
   *  its rasterized payload would be non-empty. When false the
   *  Active Layer option in the Region dropdown stays disabled. */
  hasActiveLayer?: boolean;
  /** Visible-Layers-region gate. True when at least one layer has
   *  `visible: true`. When false the Visible Layers option in the
   *  Region dropdown stays disabled (exporting a fully-hidden
   *  canvas would produce an empty file). */
  hasAnyVisibleLayer?: boolean;
  /** Name of the currently-active layer (e.g. "Hero Banner"). Used
   *  only to seed the File Name input's default when the user picks
   *  the Active Layer region — the suggestion becomes
   *  `${defaultFileName}-layer-<slug>`. Optional; falls back to
   *  "Layer" when omitted. */
  activeLayerName?: string;
  /** Optional helper that produces a blob at the current settings.
   *  Used only for the estimated-size readout — the dialog never
   *  saves this blob anywhere. When undefined, the dialog skips
   *  the size estimate entirely (rather than emitting "?"). Skipped
   *  for PSD format (estimate would require full export merge). */
  onEstimateBlob?: (
    format: CanvasNativeExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => Promise<Blob | null>;
  defaultFileName: string;
  canvasWidth: number;
  canvasHeight: number;
  /**
   * Pre-select the destination toggle. Used when the dialog is opened from
   * the close-confirmation flow with a chosen destination.
   */
  defaultDestination?: ExportDestination;
  /**
   * Pre-select the format dropdown. Defaults to `'png'`. EditorPage
   * derives this from the active tab's source file (handle or
   * filename extension) so opening a JPEG and choosing Save / Save As
   * lands the dialog on JPEG instead of PNG — the user's source
   * format is almost always what they want to write back as.
   */
  defaultFormat?: ExportFormat;
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function ExportDialog({
  onClose,
  onExportLocal,
  onExportFias,
  onExportClipboard,
  onExportPsd,
  onExportSvg,
  onExportPdf,
  onExportSlices,
  sliceCount = 0,
  onExportRegion,
  hasMarqueeSelection = false,
  hasActiveLayer = false,
  hasAnyVisibleLayer = false,
  activeLayerName,
  onEstimateBlob,
  defaultFileName,
  canvasWidth,
  canvasHeight,
  defaultDestination = 'local',
  defaultFormat = 'png',
}: ExportDialogProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [format, setFormat] = useState<ExportFormat>(defaultFormat);
  const [quality, setQuality] = useState(92);
  const [width, setWidth] = useState(canvasWidth);
  const [height, setHeight] = useState(canvasHeight);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [scaleMode, setScaleMode] = useState<ScaleMode>('1x');
  const [region, setRegion] = useState<RegionMode>('whole');
  /** Preset dropdown value. Built-in IDs are plain strings; user
   *  presets are encoded as `user:<uuid>` so the dropdown can route
   *  selection by prefix. CUSTOM_PRESET is the no-match sentinel. */
  const [presetId, setPresetId] = useState<string>(CUSTOM_PRESET);
  const [userPresets, setUserPresets] = useState<UserExportPreset[]>([]);
  const [savePresetMode, setSavePresetMode] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [destination, setDestination] = useState<ExportDestination>(defaultDestination);
  const [isExporting, setIsExporting] = useState(false);
  const [estimatedBytes, setEstimatedBytes] = useState<number | null>(null);
  const [isEstimating, setIsEstimating] = useState(false);

  // Accordion state shared across desktop + mobile. Format,
  // Dimensions, and Save To open by default — these cover the
  // common-case "pick a format, accept the dims, hit Save" flow
  // without scrolling. Scale, Region, and Preset start collapsed
  // (advanced controls a user only reaches for occasionally).
  // Each section's chevron toggles its `open` flag in either
  // viewport — the prior desktop-always-expanded behavior was a
  // hold-over from when the dialog was shorter; with Region +
  // Slices + Preset surfaces added the dialog now overflows
  // typical desktop viewports too.
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>({
    format: true,
    dimensions: true,
    destination: true,
    scale: false,
    region: false,
    preset: false,
  });

  // The third destination is "Share" on mobile (where the native
  // share sheet is the right affordance for "send this to another
  // app") and "Copy to clipboard" on desktop. Same outcome from the
  // dialog's perspective — the parent picks the right path.
  const clipboardDestinationLabel =
    IS_COARSE_POINTER && SUPPORTS_FILE_SHARE ? 'Share' : 'Copy to clipboard';
  const showClipboardDestination =
    typeof onExportClipboard === 'function' && SUPPORTS_CLIPBOARD_OR_SHARE;

  const aspectRatio = canvasWidth / canvasHeight;

  const handleWidthChange = (w: number) => {
    setWidth(w);
    if (maintainAspectRatio) {
      setHeight(Math.round(w / aspectRatio));
    }
    setScaleMode('custom');
  };

  const handleHeightChange = (h: number) => {
    setHeight(h);
    if (maintainAspectRatio) {
      setWidth(Math.round(h * aspectRatio));
    }
    setScaleMode('custom');
  };

  // When the user picks 1x/2x/3x, derive width/height from the
  // canvas size and lock the ratio. Custom doesn't change anything
  // — it just frees the inputs.
  const handleScaleChange = (mode: ScaleMode) => {
    setScaleMode(mode);
    if (mode !== 'custom') {
      const m = SCALE_MULTIPLIERS[mode];
      setWidth(canvasWidth * m);
      setHeight(canvasHeight * m);
      setMaintainAspectRatio(true);
      setPresetId(CUSTOM_PRESET);
    }
  };

  // Preset selection sets absolute dimensions and locks the ratio
  // to the preset's intrinsic ratio. Scale flips to custom because
  // preset dims rarely match a canvas multiple. Accepts either a
  // built-in preset id, the CUSTOM_PRESET sentinel, OR a `user:<id>`
  // encoded user-preset id.
  const handlePresetChange = (id: string) => {
    setPresetId(id);
    if (id === CUSTOM_PRESET) return;
    if (id.startsWith('user:')) {
      const userId = id.slice('user:'.length);
      const userPreset = userPresets.find((p) => p.id === userId);
      if (!userPreset) {
        // Stale id — the preset was deleted in another tab, or the
        // dropdown was programmatically set to a value before
        // `listUserPresets()` resolved. Snap back to CUSTOM_PRESET
        // rather than leaving the dropdown displaying a phantom
        // selection. (Pre-PR review audit Minor #2.)
        setPresetId(CUSTOM_PRESET);
        return;
      }
      setWidth(userPreset.width);
      setHeight(userPreset.height);
      setMaintainAspectRatio(true);
      setScaleMode('custom');
      // Optional format flip — only when the saved preset specifies one.
      // Most users save presets for dimensions, not encoding; keeping
      // the current format is the less-surprising default when no
      // format was saved.
      if (userPreset.format && isWiredFormat(userPreset.format)) {
        setFormat(userPreset.format);
      }
      return;
    }
    const preset = findBuiltInPreset(id as BuiltInPresetId);
    if (!preset) return;
    setWidth(preset.width);
    setHeight(preset.height);
    setMaintainAspectRatio(true);
    setScaleMode('custom');
  };

  // Load user presets on mount. Best-effort — failure leaves the
  // dropdown showing built-ins only, which matches the pre-#8
  // behavior.
  useEffect(() => {
    let cancelled = false;
    listUserPresets().then((presets) => {
      if (!cancelled) setUserPresets(presets);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep preset dropdown in sync when the user types dimensions
  // that happen to land on a built-in OR user preset's size. Built-ins
  // win on tie (they ship with curated labels); user-preset dim
  // matches only surface when no built-in matches.
  useEffect(() => {
    const builtIn = matchPresetByDimensions(width, height);
    if (builtIn) {
      setPresetId(builtIn.id);
      return;
    }
    const userMatch = userPresets.find((p) => p.width === width && p.height === height);
    setPresetId(userMatch ? `user:${userMatch.id}` : CUSTOM_PRESET);
  }, [width, height, userPresets]);

  /** Persist the current dimensions (and optionally format) as a
   *  user preset. Name defaults to `${width}×${height}` when blank.
   *  Refreshes the dropdown list on success. */
  const commitSavePreset = useCallback(async () => {
    const saved = await saveUserPreset({
      name: savePresetName,
      width,
      height,
      // Don't bake the user's current format into the preset by
      // default — they're usually saving a "Twitter card size" not
      // a "Twitter card size as WebP". A future "Save format too"
      // checkbox could surface the option; v1 keeps it implicit.
    });
    if (saved) {
      setUserPresets((prev) => [saved, ...prev]);
      // Auto-select the preset we just saved so the dropdown shows
      // the user their work landed.
      setPresetId(`user:${saved.id}`);
    }
    setSavePresetMode(false);
    setSavePresetName('');
  }, [savePresetName, width, height]);

  /** Delete the currently-selected user preset. No-op for built-ins
   *  / Custom; the button is hidden in those cases. */
  const handleDeleteSelectedPreset = useCallback(async () => {
    if (!presetId.startsWith('user:')) return;
    const userId = presetId.slice('user:'.length);
    await deleteUserPreset(userId);
    setUserPresets((prev) => prev.filter((p) => p.id !== userId));
    setPresetId(CUSTOM_PRESET);
  }, [presetId]);

  // Destination gating by format:
  //  - TIFF is strictly local-only (no Fias upload path, no clipboard).
  //  - Structured formats (PSD / SVG / PDF) now support local download
  //    AND Save-to-Fias (via the dedicated `/save-structured` upload),
  //    but NOT the OS clipboard (their bytes aren't a clipboard image
  //    type). Force the destination back to a reachable one when the
  //    current pick can't carry the chosen format — otherwise hitting
  //    Save would surface a confusing destructive toast for a state the
  //    user didn't realize they were in.
  useEffect(() => {
    if (format === 'tiff' && destination !== 'local') {
      setDestination('local');
    } else if (isStructuredFormat(format) && destination === 'clipboard') {
      setDestination('local');
    }
  }, [format, destination]);

  // Defensive: if `format` ever becomes 'avif' on a browser that
  // can't encode AVIF, snap it back to PNG. The dropdown's
  // `disabled` attribute prevents normal selection of AVIF on
  // unsupported browsers, but this guards against any other path
  // that could set state — a future persistence layer reloading a
  // saved AVIF preference, a programmatic setFormat, or a tampered
  // DOM bypassing the disabled flag. Without this, the dialog
  // would show AVIF selected on a disabled option (visually
  // confusing) and Save would silently fail at encode time.
  useEffect(() => {
    if (format === 'avif' && !SUPPORTS_AVIF) {
      setFormat('png');
    }
  }, [format]);

  // Tier 2 Group F sub-piece #5 — when Region is Slices:
  //   1. Force the format to one that can decompose into per-slice
  //      files. PSD / SVG carry layered structure that doesn't
  //      decompose (slicing them produces N copies of the same
  //      layered file, which is rarely the user's intent). PDF /
  //      canvas-native rasters decompose cleanly — each slice
  //      becomes its own file inside the ZIP. Force PNG so the
  //      user picks again from a valid set when the current
  //      format is slices-incompatible.
  //   2. Force destination to 'local' or 'fias' — clipboard / share
  //      can only carry a single payload usefully, so the Slices
  //      region's multi-file output collapses to confusion there.
  //      Auto-flip mirrors the structured-format pattern above.
  useEffect(() => {
    if (region !== 'slices') return;
    if (isSlicesIncompatibleFormat(format)) {
      setFormat('png');
    }
    if (destination === 'clipboard') {
      setDestination('local');
    }
  }, [region, format, destination]);

  // Sub-region modes (Selection / Active Layer / Visible Layers) —
  // each produces a single flat raster, so the structural-format
  // constraint is the same as Slices: PSD / SVG can't be derived
  // from a sub-region without a separate filtering pass through the
  // PSD / SVG encoders, which is out of v1 scope. PDF / TIFF / the
  // canvas-native rasters all work. Reuse `isSlicesIncompatibleFormat`
  // — the rejection set is identical. Unlike Slices, the destination
  // is NOT forced: clipboard / share works fine for a single image.
  useEffect(() => {
    if (region !== 'selection' && region !== 'active-layer' && region !== 'visible') return;
    if (isSlicesIncompatibleFormat(format)) {
      setFormat('png');
    }
  }, [region, format]);

  // Per-region File Name default. Picking a region updates the File
  // Name input to `${defaultFileName}${suffix}` — but ONLY while the
  // user hasn't customized the input. As soon as they type into it,
  // we lock in their value: subsequent region picks leave the input
  // alone, on the principle that an explicit user choice shouldn't
  // be silently rewritten. The customization flag stays true for the
  // lifetime of this dialog instance — closing and reopening resets
  // it (the dialog unmounts).
  //
  // A `useRef` (not state) tracks the flag so the effect's deps
  // array stays clean. If `userEditedFileNameRef` were state we'd
  // either need it as a dep (causing the effect to re-fire and
  // clobber the user's edit the moment they typed) or stale-closure
  // around it. The ref reads the latest value on every fire and
  // never triggers a re-render itself.
  const userEditedFileNameRef = useRef(false);
  useEffect(() => {
    if (userEditedFileNameRef.current) return;
    let suffix = '';
    if (region === 'selection') suffix = '-selection';
    else if (region === 'visible') suffix = '-visible';
    else if (region === 'active-layer') {
      const slug = slugifySliceName(activeLayerName ?? 'Layer');
      suffix = `-layer-${slug}`;
    }
    setFileName(`${defaultFileName}${suffix}`);
  }, [region, defaultFileName, activeLayerName]);

  // Debounced estimated-size readout. We trigger a real toBlob at
  // the current settings so the readout reflects compressor
  // behavior (PNG zlib level, JPEG quality curve) rather than a
  // raw pixel-count guess. 300ms debounce — covers typing in W/H
  // and slider drags without spamming the encoder.
  const estimateSeqRef = useRef(0);
  useEffect(() => {
    // PSD and SVG formats skip the estimate — running the full export
    // merge every 300ms (load stash + re-parse + walk + serialize) is
    // too heavy. Sub-region modes (Selection / Active Layer / Visible
    // Layers) also skip — the dialog doesn't have access to the
    // sub-canvas, and rendering one per debounce tick is heavier than
    // the readout's worth. Slices also can't estimate meaningfully
    // (output is N files of varying sizes) — handled by the existing
    // skip below. The readout falls back to '—' or hidden depending
    // on whether `onEstimateBlob` is provided.
    if (
      !onEstimateBlob ||
      isStructuredFormat(format) ||
      region === 'selection' ||
      region === 'active-layer' ||
      region === 'visible'
    ) {
      setEstimatedBytes(null);
      return;
    }
    const rasterFormat = format as CanvasNativeExportFormat;
    const seq = ++estimateSeqRef.current;
    setIsEstimating(true);
    const handle = setTimeout(async () => {
      try {
        const target =
          width === canvasWidth && height === canvasHeight ? undefined : { width, height };
        const blob = await onEstimateBlob(rasterFormat, quality / 100, target);
        // Drop the result if a newer estimate has been scheduled —
        // otherwise stale callbacks could clobber a fresher number.
        if (seq !== estimateSeqRef.current) return;
        setEstimatedBytes(blob ? blob.size : null);
      } catch {
        if (seq !== estimateSeqRef.current) return;
        setEstimatedBytes(null);
      } finally {
        if (seq === estimateSeqRef.current) setIsEstimating(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [onEstimateBlob, format, quality, width, height, canvasWidth, canvasHeight, region]);

  const toggleSection = useCallback((id: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  /** Validate the file name + dimension inputs before kicking off
   *  the export. `type="number"` doesn't prevent empty string (parses
   *  to 0) or paste-of-NaN, and `min={1}` doesn't enforce on submit.
   *  Failed validation sets a transient error string surfaced inline
   *  below the Save button — no toast, no silent encoder crash. */
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    // Sanitize / validate file name — strip leading/trailing whitespace,
    // reject empty, reject OS-illegal characters (`/`, `\`, NUL).
    const trimmed = fileName.trim();
    if (!trimmed) {
      setExportError('Please enter a file name.');
      return;
    }
    if (/[\\/\0]/.test(trimmed)) {
      setExportError('File name cannot contain slashes or null characters.');
      return;
    }
    // Sub-region pre-flight (Selection / Active Layer / Visible
    // Layers). Each produces a single image at the region's natural
    // pixel bbox — Dimensions W/H are irrelevant (skip validation),
    // Scale radio applies, format must be sub-region-compatible
    // (PSD / SVG rejected for the same reason they're rejected for
    // Slices: no per-region filtering in those encoders yet).
    // Unlike Slices, clipboard / share is allowed — the output is a
    // single image so it carries fine via `navigator.clipboard.write`.
    if (region === 'selection' || region === 'active-layer' || region === 'visible') {
      if (!onExportRegion) {
        setExportError('This region cannot be exported in this context.');
        return;
      }
      // Per-mode prerequisites — defensive even though the
      // dropdown's runtime gate prevents picking an unavailable
      // option. A future surface could programmatically set
      // `region` past the gate; fail closed with a clear message.
      if (region === 'selection' && !hasMarqueeSelection) {
        setExportError('Selection region requires a finalized marquee selection.');
        return;
      }
      if (region === 'active-layer' && !hasActiveLayer) {
        setExportError('Active Layer region requires an active layer with content.');
        return;
      }
      if (region === 'visible' && !hasAnyVisibleLayer) {
        setExportError('Visible Layers region requires at least one visible layer.');
        return;
      }
      if (isSlicesIncompatibleFormat(format)) {
        // The format-flip effect runs after React commits, so a user
        // who picked a sub-region + PSD/SVG and clicked Save before
        // the effect ran could land here. Same defensive idiom as
        // the slices pre-flight.
        setExportError(
          'Selection / Active Layer / Visible Layers cannot export PSD or SVG. Pick a single-image format.',
        );
        return;
      }
      const scaleMultiplier = scaleMode === 'custom' ? 1 : SCALE_MULTIPLIERS[scaleMode];
      // `trimmed` is the live File Name input value — the dialog's
      // per-region useEffect seeds it with the region's natural
      // default (`-selection` / `-layer-<slug>` / `-visible`) when
      // the user picks a region, and the user can edit freely from
      // there. The handler uses whatever's in the input verbatim.
      setExportError(null);
      setIsExporting(true);
      try {
        await onExportRegion({
          region,
          destination,
          format,
          quality: quality / 100,
          scaleMultiplier,
          fileName: trimmed,
        });
      } finally {
        setIsExporting(false);
      }
      onClose();
      return;
    }
    // Tier 2 Group F sub-piece #5 — Slices region pre-flight. The
    // slice export uses each slice's natural pixel size, so the
    // Dimensions section's W/H is irrelevant; we skip the
    // dimensions validation entirely for this region. Sliced
    // export still respects the Scale radio (1x/2x/3x), which is
    // applied per-slice in `exportSlicesAsBlobs`.
    if (region === 'slices') {
      if (!onExportSlices) {
        setExportError('Slice export is not available in this context.');
        return;
      }
      if (sliceCount === 0) {
        setExportError('Define at least one slice with the Slice tool first.');
        return;
      }
      if (isSlicesIncompatibleFormat(format)) {
        // The format-flip effect runs after React commits, so a
        // user who picked Slices region + PSD/SVG and clicked Save
        // before the effect ran could land here. Coerce to PNG
        // rather than rejecting — same defensive idiom as the PSD
        // destination flip below.
        setExportError('Slices region cannot export PSD or SVG. Pick a single-image format.');
        return;
      }
      if (destination === 'clipboard') {
        setExportError('Slices region cannot be sent to the clipboard. Choose Local or Fias.');
        return;
      }
      const scaleMultiplier = scaleMode === 'custom' ? 1 : SCALE_MULTIPLIERS[scaleMode];
      setExportError(null);
      setIsExporting(true);
      try {
        await onExportSlices({
          destination,
          format,
          quality: quality / 100,
          scaleMultiplier,
          fileName: trimmed,
        });
      } finally {
        setIsExporting(false);
      }
      onClose();
      return;
    }
    // Validate dimensions — must be positive finite integers in a
    // sensible range. The canvas encoder rejects NaN / Infinity but
    // catching here gives a clear UI error rather than a generic
    // encoder failure.
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width < 1 ||
      height < 1 ||
      width > 32000 ||
      height > 32000
    ) {
      setExportError('Dimensions must be whole numbers between 1 and 32000 pixels.');
      return;
    }
    // PSD and SVG are gated on their respective callbacks being
    // wired. The dropdown disables them when the props aren't
    // provided, but defensively refuse to proceed if the dialog is
    // somehow in that state without the handler.
    if (format === 'psd' && !onExportPsd) {
      setExportError('PSD export is not available in this context.');
      return;
    }
    if (format === 'svg' && !onExportSvg) {
      setExportError('SVG export is not available in this context.');
      return;
    }
    if (format === 'pdf' && !onExportPdf) {
      setExportError('PDF export is not available in this context.');
      return;
    }
    setExportError(null);
    setIsExporting(true);
    try {
      // PSD + SVG take dedicated routes — they can't roundtrip through
      // canvas.toBlob, have no quality parameter, and their dimensions
      // are determined by the editor's canvas (not the dialog's Scale /
      // Custom W×H — those don't apply to layered structured output).
      // Destination is force-normalized to 'local' here because the
      // auto-flip effect runs AFTER React commits; if the user changes
      // format to PSD/SVG and immediately clicks Save, the destination
      // state can still hold the pre-flip value. Both only support
      // local download in v1, so force it.
      // PSD / SVG / PDF take dedicated routes — they can't roundtrip
      // through canvas.toBlob, have no quality parameter, and their
      // dimensions are determined by the editor's canvas (not the
      // dialog's Scale / Custom W×H — those don't apply to layered
      // structured output). NO early-returns here — every successful
      // path needs to fall through to `onClose()` below so the dialog
      // dismisses after save. Previous version used `return` after
      // each structured branch and the dialog stayed mounted for
      // PSD / SVG / PDF saves (2026-05-26 user report).
      if (format === 'psd' && onExportPsd) {
        await onExportPsd(trimmed, destination);
      } else if (format === 'svg' && onExportSvg) {
        await onExportSvg(trimmed, destination);
      } else if (format === 'pdf' && onExportPdf) {
        await onExportPdf(trimmed, destination);
      } else if (isStructuredFormat(format)) {
        // Use explicit narrowing rather than `as` so a future
        // control-flow change can't silently let a structured format
        // leak into the canvas-native callbacks.
        throw new Error(`Unreachable: ${format} must route via its dedicated callback`);
      } else {
        // Raster path.
        const rasterFormat: CanvasNativeExportFormat = format;
        // Only forward target dimensions when they actually differ
        // from the source canvas — otherwise the helper passes the
        // native image resolution path through unchanged.
        const target =
          width === canvasWidth && height === canvasHeight ? undefined : { width, height };
        if (destination === 'local') {
          await onExportLocal(trimmed, rasterFormat, quality / 100, target);
        } else if (destination === 'fias') {
          await onExportFias(
            { format: rasterFormat, quality, width, height, maintainAspectRatio },
            trimmed,
          );
        } else if (destination === 'clipboard' && onExportClipboard) {
          await onExportClipboard(trimmed, rasterFormat, quality / 100, target);
        }
      }
    } finally {
      setIsExporting(false);
    }
    onClose();
  };

  const sizeReadout = useMemo(() => {
    if (!onEstimateBlob) return null;
    if (estimatedBytes === null) return isEstimating ? 'Estimating…' : '—';
    return `≈ ${formatByteSize(estimatedBytes)}`;
  }, [onEstimateBlob, estimatedBytes, isEstimating]);

  return (
    <div className={MODAL_OVERLAY}>
      <div
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto`}
      >
        <h2 className="text-lg font-semibold">Save Image As&hellip;</h2>

        {/* File name — always visible, not a section. */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">File name</span>
          <input
            type="text"
            value={fileName}
            onChange={(e) => {
              setFileName(e.target.value);
              // Lock in the user's intent — subsequent region picks
              // won't overwrite their typed value. See the effect
              // above for the full mental model.
              userEditedFileNameRef.current = true;
            }}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          />
        </label>

        {/* Format */}
        <Section id="format" title="Format" open={openSections.format} onToggle={toggleSection}>
          <select
            value={format}
            onChange={(e) => {
              const v = e.target.value;
              if (isWiredFormat(v)) setFormat(v);
            }}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
            aria-label="Format"
          >
            {EXPORT_FORMAT_OPTIONS.map((opt) => {
              // AVIF is wired in code but only usable when the host
              // browser can encode it. Re-disable the option with a
              // browser-specific hint when feature detection fails —
              // honest UX (user sees why the option is unavailable)
              // instead of letting them pick a format that silently
              // fails at encode time.
              const isAvifFallback = opt.value === 'avif' && !SUPPORTS_AVIF;
              // Tier 2 Group F sub-piece #5 + #6 — Slices region
              // supports canvas-native rasters AND PDF (each slice
              // becomes its own .pdf in the ZIP). PSD / SVG carry
              // layered structure that doesn't decompose into
              // per-region files, so they stay gated. Use the
              // dedicated guard rather than `isStructuredFormat` so
              // PDF participates here while still benefiting from
              // the structured-format treatment everywhere else
              // (destination lock, quality-slider hide, etc.).
              //
              // Same rationale extends to the sub-region modes
              // (Selection / Active Layer / Visible Layers): each
              // produces a single flat raster, and PSD / SVG don't
              // have per-region filtering in their encoders yet.
              const subRegionGate =
                (region === 'slices' ||
                  region === 'selection' ||
                  region === 'active-layer' ||
                  region === 'visible') &&
                (opt.value === 'psd' || opt.value === 'svg');
              const disabled = opt.disabled || isAvifFallback || subRegionGate;
              // Pick the hint surface: registry hint > AVIF browser
              // fallback > sub-region gate explanation. Only one
              // disables a given option in practice (the registry's
              // `disabled` covers "not yet wired", the AVIF gate
              // covers a wired format the browser can't run, the
              // sub-region gate covers a region-specific structural
              // mismatch — slices OR Selection/Active Layer/Visible
              // Layers vs PSD/SVG), so hint precedence matches the
              // disabled cause.
              let hint = '';
              if (opt.hint) hint = ` (${opt.hint})`;
              else if (isAvifFallback) hint = ' (not supported in this browser)';
              else if (subRegionGate) hint = ' (not available for this region)';
              return (
                <option key={opt.value} value={opt.value} disabled={disabled}>
                  {opt.label}
                  {hint}
                </option>
              );
            })}
          </select>

          {/* Quality (for lossy formats — PNG is lossless and structured
              formats — PSD, SVG, future PDF / TIFF — have no quality
              parameter; their encoders write structural data). */}
          {!isLosslessRasterFormat(format) && !isStructuredFormat(format) && (
            <label className="block space-y-1 mt-3">
              <span className="text-sm font-medium">Quality: {quality}%</span>
              <input
                type="range"
                min={10}
                max={100}
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="w-full"
                aria-label="Quality"
              />
            </label>
          )}
          {format === 'psd' && (
            <p role="status" className="text-xs text-muted-foreground mt-3">
              PSD preserves layers, masks, and original PSD layer data losslessly. Save to local
              download or Fias files; Copy to clipboard doesn’t support PSD.
            </p>
          )}
          {format === 'svg' && (
            <p role="status" className="text-xs text-muted-foreground mt-3">
              SVG preserves vector paths, primitives, groups, and original SVG features (text,
              filters, gradients) losslessly when the file was imported as SVG. Save to local
              download or Fias files; Copy to clipboard doesn’t support SVG.
            </p>
          )}
          {format === 'pdf' && (
            <p role="status" className="text-xs text-muted-foreground mt-3">
              PDF exports the canvas as a single-page document at native size, with the image
              embedded as JPEG. Save to local download or Fias files; Copy to clipboard doesn’t
              support PDF.
            </p>
          )}
          {format === 'tiff' && (
            <p role="status" className="text-xs text-muted-foreground mt-3">
              TIFF exports the canvas as an uncompressed raster — lossless, large file size. TIFF is
              encoded via the bundled utif library (browsers don’t encode TIFF natively); for that
              reason Save to Fias and Copy to clipboard are restricted to local download only in
              this version.
            </p>
          )}
        </Section>

        {/* Dimensions */}
        <Section
          id="dimensions"
          title="Dimensions"
          open={openSections.dimensions}
          onToggle={toggleSection}
        >
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={width}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              disabled={region !== 'whole'}
              // When region is anything other than 'whole', the
              // inputs are disabled because the region's natural
              // pixel bbox supersedes the dialog's W/H. Point
              // screen readers at the explanation paragraph below
              // (rendered conditionally for the same `region !==
              // 'whole'` branches) so the "disabled" announcement
              // arrives with a reason attached. (Pre-PR review a11y
              // finding.)
              aria-describedby={region !== 'whole' ? 'dims-region-note' : undefined}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              min={1}
              aria-label="Width"
            />
            <span className="text-muted-foreground">x</span>
            <input
              type="number"
              value={height}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              disabled={region !== 'whole'}
              aria-describedby={region !== 'whole' ? 'dims-region-note' : undefined}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              min={1}
              aria-label="Height"
            />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
          <label className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={maintainAspectRatio}
              onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              disabled={region !== 'whole'}
              aria-describedby={region !== 'whole' ? 'dims-region-note' : undefined}
              className="rounded"
            />
            <span className="text-xs text-muted-foreground">Maintain aspect ratio</span>
          </label>
          {region === 'slices' && (
            <p id="dims-region-note" role="status" className="text-xs text-muted-foreground mt-2">
              Each slice exports at its natural pixel size. The Scale below applies uniformly to
              every slice.
            </p>
          )}
          {(region === 'selection' || region === 'active-layer' || region === 'visible') && (
            <p id="dims-region-note" role="status" className="text-xs text-muted-foreground mt-2">
              {region === 'selection'
                ? 'The selection exports at its natural bbox size.'
                : region === 'active-layer'
                  ? 'The active layer exports at its natural bbox size.'
                  : 'Visible layers export at the full canvas size.'}{' '}
              The Scale below applies to the output.
            </p>
          )}
        </Section>

        {/* Destination — placed directly under Dimensions so the
            common "pick a format, accept the dims, pick where to
            save, hit Save" flow flows top-to-bottom without
            scrolling past Scale / Region / Preset. */}
        <Section
          id="destination"
          title="Save to"
          open={openSections.destination}
          onToggle={toggleSection}
        >
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                destination === 'local'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('local')}
            >
              Local download
            </button>
            <button
              type="button"
              // Fias supports raster + structured (PSD/SVG/PDF via the
              // dedicated upload route). Only TIFF has no Fias path.
              disabled={format === 'tiff'}
              className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                destination === 'fias'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={() => setDestination('fias')}
              title={format === 'tiff' ? 'TIFF export — local download only' : undefined}
            >
              Fias files
            </button>
            {showClipboardDestination && (
              <button
                type="button"
                disabled={isClipboardUnsupportedFormat(format) || region === 'slices'}
                className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                  destination === 'clipboard'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                onClick={() => setDestination('clipboard')}
                title={
                  region === 'slices'
                    ? 'Slices region — local download or Fias only'
                    : isClipboardUnsupportedFormat(format)
                      ? `${format.toUpperCase()} export — local download only`
                      : undefined
                }
              >
                {clipboardDestinationLabel}
              </button>
            )}
          </div>
          {destination === 'clipboard' &&
            format !== 'png' &&
            SUPPORTS_CLIPBOARD_WRITE &&
            !IS_COARSE_POINTER && (
              <p className="text-xs text-muted-foreground mt-2">
                Most browsers only support PNG on the clipboard. Switch format if the copy fails.
              </p>
            )}
        </Section>

        {/* Scale */}
        <Section id="scale" title="Scale" open={openSections.scale} onToggle={toggleSection}>
          <div role="radiogroup" aria-label="Scale" className="flex gap-2 flex-wrap">
            {(['1x', '2x', '3x', 'custom'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={scaleMode === mode}
                onClick={() => handleScaleChange(mode)}
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  scaleMode === mode
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
              >
                {mode === 'custom' ? 'Custom' : mode}
              </button>
            ))}
          </div>
        </Section>

        {/* Region */}
        <Section id="region" title="Region" open={openSections.region} onToggle={toggleSection}>
          <select
            value={region}
            onChange={(e) => {
              const v = e.target.value;
              if (isWiredRegion(v)) setRegion(v);
            }}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
            aria-label="Region"
          >
            {REGION_OPTIONS.map((opt) => {
              // Per-mode runtime gates. Each new region (Selection /
              // Active Layer / Visible Layers) needs both its
              // prerequisite (selection present / active layer set /
              // any layer visible) AND `onExportRegion` to be wired
              // — the handler is optional at the prop level so a
              // future surface that doesn't support sub-region
              // export can omit it and the options stay disabled.
              // Slices follows the same shape with its own gate
              // (sliceCount + onExportSlices).
              const isSlicesOption = opt.value === 'slices';
              const sliceUnavailable = isSlicesOption && (sliceCount === 0 || !onExportSlices);
              const isSelectionOption = opt.value === 'selection';
              const selectionUnavailable =
                isSelectionOption && (!hasMarqueeSelection || !onExportRegion);
              const isActiveLayerOption = opt.value === 'active-layer';
              const activeLayerUnavailable =
                isActiveLayerOption && (!hasActiveLayer || !onExportRegion);
              const isVisibleOption = opt.value === 'visible';
              const visibleUnavailable =
                isVisibleOption && (!hasAnyVisibleLayer || !onExportRegion);
              const disabled =
                opt.disabled ||
                sliceUnavailable ||
                selectionUnavailable ||
                activeLayerUnavailable ||
                visibleUnavailable;
              // User-facing hint surfaces the reason the option is
              // disabled. Slices shows its count even when enabled
              // ("Slices (3)") so the user doesn't have to switch
              // tools to remember how many regions will export.
              let suffix = '';
              if (isSlicesOption) {
                suffix = sliceCount > 0 ? ` (${sliceCount})` : ' — none defined';
              } else if (selectionUnavailable && !hasMarqueeSelection) {
                suffix = ' — no selection';
              } else if (activeLayerUnavailable && !hasActiveLayer) {
                suffix = ' — no active layer';
              } else if (visibleUnavailable && !hasAnyVisibleLayer) {
                suffix = ' — no visible layers';
              }
              return (
                <option key={opt.value} value={opt.value} disabled={disabled}>
                  {opt.label}
                  {suffix}
                </option>
              );
            })}
          </select>
        </Section>

        {/* Preset */}
        <Section id="preset" title="Preset" open={openSections.preset} onToggle={toggleSection}>
          <div className="flex gap-2 items-center">
            <select
              value={presetId}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-md"
              aria-label="Preset"
            >
              <option value={CUSTOM_PRESET}>Custom</option>
              {BUILT_IN_EXPORT_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} ({p.width}×{p.height})
                </option>
              ))}
              {/* Saved-presets group — only render when the user
                  has any. The <optgroup> visually separates the
                  built-ins from the user's list without needing a
                  custom dropdown widget. */}
              {userPresets.length > 0 && (
                <optgroup label="Saved presets">
                  {userPresets.map((p) => (
                    <option key={p.id} value={`user:${p.id}`}>
                      {p.name} ({p.width}×{p.height})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {/* When a user preset is currently selected, surface a
                Delete affordance. (Native <select> can't show an ×
                on each row; placing the action next to the dropdown
                is the WAI-friendly fallback. A custom dropdown
                widget with per-row ×s is a future enhancement.) */}
            {presetId.startsWith('user:') && (
              <button
                type="button"
                onClick={handleDeleteSelectedPreset}
                title="Delete this preset"
                aria-label="Delete selected preset"
                className="px-2 py-2 text-xs rounded-md border border-border text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
              >
                ×
              </button>
            )}
            {!savePresetMode && (
              <button
                type="button"
                onClick={() => setSavePresetMode(true)}
                title="Save the current dimensions as a preset"
                className="px-3 py-2 text-xs rounded-md border border-border hover:bg-accent"
              >
                Save current as preset…
              </button>
            )}
          </div>
          {savePresetMode && (
            <div className="flex gap-2 items-center mt-2">
              <input
                type="text"
                autoFocus
                value={savePresetName}
                maxLength={USER_EXPORT_PRESET_NAME_MAX}
                onChange={(e) => setSavePresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitSavePreset();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setSavePresetMode(false);
                    setSavePresetName('');
                  }
                }}
                placeholder={`Preset name (default: ${width}×${height})`}
                aria-label="New preset name"
                className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-md"
              />
              <button
                type="button"
                onClick={commitSavePreset}
                className="px-3 py-2 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setSavePresetMode(false);
                  setSavePresetName('');
                }}
                className="px-3 py-2 text-xs rounded-md border border-border text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
            </div>
          )}
        </Section>

        {/* Estimated size */}
        {sizeReadout && (
          <div className="text-xs text-muted-foreground">
            Estimated size: <span aria-label="Estimated size">{sizeReadout}</span>
          </div>
        )}

        {/* Validation error — surfaced inline above the action buttons. */}
        {exportError && (
          <div
            role="alert"
            className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2"
          >
            {exportError}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One collapsible section in the dialog. The chevron toggles
 *  `open` in either viewport — desktop used to render every
 *  section expanded regardless of state, but that left the dialog
 *  taller than typical viewports once Region / Slices / Preset
 *  were added. Treating `open` as authoritative everywhere lets
 *  the dialog start compact and the user expand only the sections
 *  they need.
 *
 *  The header is always a button so screen readers can navigate
 *  sections consistently. The controlled element stays in the DOM
 *  when collapsed (with `hidden` so it's removed from layout +
 *  the accessibility tree) so `aria-controls` always resolves to
 *  an existing target per WAI-ARIA's contract. */
function Section({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: SectionId;
  title: string;
  open: boolean;
  onToggle: (id: SectionId) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded-md">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium hover:bg-accent/50"
        aria-expanded={open}
        aria-controls={`export-section-${id}`}
      >
        <span>{title}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      <div id={`export-section-${id}`} className="px-3 pb-3" hidden={!open}>
        {children}
      </div>
    </div>
  );
}
